// Geo-state detection for Tena AU — multi-signal cascade with concordance.
//
// Drives subtask #685 (Geo IP Lookup + Global State) of #417 Stock Status by Region/State.
//
// Why multi-signal: every single detection method has well-documented gaps on
// Australian traffic. Telstra/Optus/Vodafone route mobile traffic through a
// small number of gateways so a Perth customer on mobile can geolocate to
// Sydney; timezone has macOS Sonoma + Chrome edge cases and privacy-browser
// spoofing; Shopify's native getRegion() has undocumented AU state support.
// Cross-checking 3 signals catches each method's weak spots and produces
// telemetry on which is most reliable in the real world.
//
// The signal names used across the module:
//   'timezone'     Intl.DateTimeFormat().resolvedOptions().timeZone map
//   'shopifyGeo'   window.Shopify.customerPrivacy.getRegion()
//   'ipGeo'        fetch https://ipinfo.io/json (free 50k/month, no API key,
//                  CORS open, AU regions returned as full names).
//
// The concordance rule: ≥2 agree → HIGH; all differ or only 1 valid → MEDIUM;
// no AU state resolved → NONE (callers fall back to Audrina's "no selection").

// Canonical 8 AU state codes. Order matches the Shopify location naming.
export const AU_STATE_CODES = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];

// IANA timezone → AU state. AU uses one canonical zone per state (Sydney
// covers ACT by convention; IA's fulfilment model lumps NSW+ACT together).
const TIMEZONE_TO_STATE = {
  'Australia/Sydney': 'NSW',
  'Australia/Melbourne': 'VIC',
  'Australia/Brisbane': 'QLD',
  'Australia/Adelaide': 'SA',
  'Australia/Perth': 'WA',
  'Australia/Hobart': 'TAS',
  'Australia/Darwin': 'NT',
  'Australia/Currie': 'TAS', // TAS sub-zone, retained for completeness
  'Australia/Broken_Hill': 'NSW',
  'Australia/Lindeman': 'QLD',
  'Australia/Lord_Howe': 'NSW',
};

// Normalise any input (raw code, full name, etc.) to a canonical AU state code
// or null if it isn't one of the 8.
export function normaliseAuState(input) {
  if (!input) return null;
  const upper = String(input).trim().toUpperCase();
  // Direct match
  if (AU_STATE_CODES.includes(upper)) return upper;
  // Shopify getRegion format "AUNSW" → NSW
  if (upper.startsWith('AU') && AU_STATE_CODES.includes(upper.slice(2))) return upper.slice(2);
  // Full names
  const fullName = {
    'NEW SOUTH WALES': 'NSW',
    'VICTORIA': 'VIC',
    'QUEENSLAND': 'QLD',
    'SOUTH AUSTRALIA': 'SA',
    'WESTERN AUSTRALIA': 'WA',
    'TASMANIA': 'TAS',
    'NORTHERN TERRITORY': 'NT',
    'AUSTRALIAN CAPITAL TERRITORY': 'ACT',
  };
  if (fullName[upper]) return fullName[upper];
  return null;
}

// Signal 1 — timezone. Synchronous, no network, no permission.
// Returns null if the timezone isn't in our AU map or API isn't available.
export function detectViaTimezone() {
  const result = { source: 'timezone', state: null, raw: null, error: null };
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    result.raw = tz;
    if (tz && TIMEZONE_TO_STATE[tz]) result.state = TIMEZONE_TO_STATE[tz];
  } catch (e) {
    result.error = e.message || 'Intl.DateTimeFormat unavailable';
  }
  return result;
}

// Signal 2 — Shopify's built-in customerPrivacy.getRegion().
// Returns ISO 3166-2 format like "AUNSW" when it succeeds. Requires the
// consent-tracking-api feature to be loaded first; returns '' pre-load.
// Resolved as null on timeout so the cascade doesn't block.
export function detectViaShopifyGeo({ timeoutMs = 1500 } = {}) {
  return new Promise((resolve) => {
    const result = { source: 'shopifyGeo', state: null, raw: null, error: null };

    const finish = (raw, error) => {
      result.raw = raw;
      result.state = normaliseAuState(raw);
      result.error = error;
      resolve(result);
    };

    // Fast path — API already loaded
    if (window.Shopify && window.Shopify.customerPrivacy && typeof window.Shopify.customerPrivacy.getRegion === 'function') {
      const region = window.Shopify.customerPrivacy.getRegion();
      finish(region, null);
      return;
    }

    // Slow path — need to load the feature
    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
      finish(null, 'Shopify.loadFeatures unavailable');
      return;
    }

    const timer = setTimeout(() => finish(null, 'loadFeatures timeout'), timeoutMs);

    try {
      window.Shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        (err) => {
          clearTimeout(timer);
          if (err) { finish(null, String(err)); return; }
          try {
            const region = window.Shopify.customerPrivacy.getRegion();
            finish(region, null);
          } catch (e) {
            finish(null, e.message || 'getRegion threw');
          }
        }
      );
    } catch (e) {
      clearTimeout(timer);
      finish(null, e.message || 'loadFeatures threw');
    }
  });
}

// Signal 3 — IP geolocation via ipinfo.io. Free 50k/month, no API key needed
// for the basic geolocation fields (ip, country, region, city, org, hostname),
// HTTPS native, CORS open. Response shape:
//   { ip, hostname, city, region: "Victoria", country: "AU", org: "...", ... }
export function detectViaIp({ timeoutMs = 2000 } = {}) {
  return new Promise((resolve) => {
    const result = { source: 'ipGeo', state: null, raw: null, error: null };
    const controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    const timer = setTimeout(() => {
      if (controller) controller.abort();
      result.error = 'ipinfo.io timeout';
      resolve(result);
    }, timeoutMs);

    fetch('https://ipinfo.io/json', controller ? { signal: controller.signal } : undefined)
      .then((r) => r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status)))
      .then((data) => {
        clearTimeout(timer);
        result.raw = data;
        if (data && data.country === 'AU') {
          result.state = normaliseAuState(data.region);
        }
        resolve(result);
      })
      .catch((e) => {
        clearTimeout(timer);
        if (!result.error) result.error = e.message || 'fetch failed';
        resolve(result);
      });
  });
}

// Concordance — combine up to 3 signal results into one decision.
// Returns { state, confidence, sources } where sources is the array of inputs
// (kept so callers can log telemetry).
export function concordance(results) {
  const valid = results.filter((r) => r && r.state);
  const counts = {};
  for (const r of valid) {
    counts[r.state] = (counts[r.state] || 0) + 1;
  }
  // Majority (≥2 of 3, or ≥2 of 2)
  const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  if (sorted.length && sorted[0][1] >= 2) {
    return { state: sorted[0][0], confidence: 'HIGH', sources: results };
  }
  // Only one valid signal — use it with MEDIUM confidence
  if (valid.length === 1) {
    return { state: valid[0].state, confidence: 'MEDIUM', sources: results };
  }
  // All three disagreed (each returned a different AU state) — prefer timezone
  // since it's the only signal immune to AU mobile carrier rerouting.
  if (valid.length >= 2) {
    const tz = results.find((r) => r.source === 'timezone' && r.state);
    if (tz) return { state: tz.state, confidence: 'MEDIUM', sources: results };
    return { state: valid[0].state, confidence: 'MEDIUM', sources: results };
  }
  return { state: null, confidence: 'NONE', sources: results };
}

// Entry point — run the enabled signals in parallel and return concordance.
// `opts.skipIpGeo: true` omits the IP geolocation call (use this when the
// visitor hasn't given OneTrust consent for tracking-adjacent 3rd-party
// scripts). The timezone signal is synchronous; the Shopify-native signal
// is allowed without consent because it IS Shopify's consent infrastructure.
export async function detectAuState(opts = {}) {
  const promises = [
    Promise.resolve(detectViaTimezone()),
    detectViaShopifyGeo(opts.shopify || {}),
  ];
  if (!opts.skipIpGeo) {
    promises.push(detectViaIp(opts.ipGeo || {}));
  }
  const results = await Promise.all(promises);
  return concordance(results);
}

// Emit detection telemetry to window.dataLayer (if present) for GTM / analytics.
// Kept optional so tests and non-GTM environments don't blow up.
export function emitDetectionTelemetry(result, { eventName = 'tena_state_detection' } = {}) {
  if (typeof window === 'undefined' || !result) return;
  const payload = {
    event: eventName,
    detected_state: result.state,
    confidence: result.confidence,
  };
  if (Array.isArray(result.sources)) {
    for (const s of result.sources) {
      payload[`src_${s.source}_state`] = s.state || null;
      if (s.error) payload[`src_${s.source}_error`] = s.error;
    }
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
}
